import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import { Lock, Eye, EyeOff, ArrowRight, ArrowLeft, Loader2, CheckCircle, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '@/lib/api';

interface ResetPasswordForm {
  password: string;
  confirmPassword: string;
}

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [isValidToken, setIsValidToken] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<ResetPasswordForm>();

  const password = watch('password');

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setIsVerifying(false);
        return;
      }

      try {
        const response = await api.get<{ valid: boolean }>(`/auth/verify-reset-token?token=${token}`);
        setIsValidToken(response.valid);
      } catch (error) {
        setIsValidToken(false);
      } finally {
        setIsVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const onSubmit = async (data: ResetPasswordForm) => {
    if (!token) return;

    setIsLoading(true);
    
    try {
      await api.post('/auth/reset-password', {
        token,
        password: data.password,
      });
      setResetSuccess(true);
      toast.success('Password reset successfully!');
    } catch (error: any) {
      const message = error.data?.message || 'Failed to reset password. Please try again.';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  // Loading state
  if (isVerifying) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="animate-spin text-[#6B9F54]" size={32} />
      </div>
    );
  }

  // Invalid or missing token
  if (!token || !isValidToken) {
    return (
      <div className="space-y-8">
        {/* Logo - mobile only */}
        <div className="lg:hidden text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-[#6B9F54] flex items-center justify-center shadow-sm">
              <span className="text-xl font-bold text-white">GG</span>
            </div>
            <span className="text-xl font-bold text-slate-900">Green Gold</span>
          </Link>
        </div>

        {/* Error message */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center space-y-6"
        >
          <div className="mx-auto w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
            <XCircle className="text-red-600" size={32} />
          </div>
          
          <div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Invalid or Expired Link</h2>
            <p className="text-slate-600">
              This password reset link is invalid or has expired.
            </p>
          </div>

          <div className="space-y-3">
            <Link
              to="/forgot-password"
              className="inline-flex items-center justify-center gap-2 w-full py-3 px-4 bg-[#6B9F54] hover:bg-[#5A8C43] text-white rounded-lg font-semibold text-base transition-colors shadow-sm"
            >
              Request New Reset Link
              <ArrowRight size={18} />
            </Link>
            
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#6B9F54] hover:text-[#5A8C43] transition-colors"
            >
              <ArrowLeft size={16} />
              Back to login
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  // Success state
  if (resetSuccess) {
    return (
      <div className="space-y-8">
        {/* Logo - mobile only */}
        <div className="lg:hidden text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-[#6B9F54] flex items-center justify-center shadow-sm">
              <span className="text-xl font-bold text-white">GG</span>
            </div>
            <span className="text-xl font-bold text-slate-900">Green Gold</span>
          </Link>
        </div>

        {/* Success message */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center space-y-6"
        >
          <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
            <CheckCircle className="text-green-600" size={32} />
          </div>
          
          <div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Password Reset!</h2>
            <p className="text-slate-600">
              Your password has been reset successfully. You can now log in with your new password.
            </p>
          </div>

          <motion.button
            onClick={() => navigate('/login')}
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            className="w-full py-3 px-4 bg-[#6B9F54] hover:bg-[#5A8C43] text-white rounded-lg font-semibold text-base transition-colors inline-flex items-center justify-center gap-2 shadow-sm"
          >
            Go to Login
            <ArrowRight size={18} />
          </motion.button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Logo - mobile only */}
      <div className="lg:hidden text-center mb-8">
        <Link to="/" className="inline-flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-xl bg-[#6B9F54] flex items-center justify-center shadow-sm">
            <span className="text-xl font-bold text-white">GG</span>
          </div>
          <span className="text-xl font-bold text-slate-900">Green Gold</span>
        </Link>
      </div>

      {/* Header */}
      <div className="text-center lg:text-left">
        <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-2">Set new password</h2>
        <p className="text-slate-600">
          Your new password must be at least 8 characters.
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {/* Password */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">New Password</label>
          <div className="relative">
            <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">
              <Lock size={18} />
            </div>
            <input
              type={showPassword ? 'text' : 'password'}
              {...register('password', {
                required: 'Password is required',
                minLength: {
                  value: 8,
                  message: 'Password must be at least 8 characters',
                },
              })}
              className={`w-full pl-11 pr-11 py-2.5 bg-white border rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 transition-all ${
                errors.password 
                  ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                  : 'border-gray-300 focus:ring-[#6B9F54] focus:border-[#6B9F54]'
              }`}
              placeholder="••••••••"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {errors.password && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-1.5 text-sm text-red-600"
            >
              {errors.password.message}
            </motion.p>
          )}
        </div>

        {/* Confirm Password */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Confirm Password</label>
          <div className="relative">
            <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">
              <Lock size={18} />
            </div>
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              {...register('confirmPassword', {
                required: 'Please confirm your password',
                validate: (value) => value === password || 'Passwords do not match',
              })}
              className={`w-full pl-11 pr-11 py-2.5 bg-white border rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 transition-all ${
                errors.confirmPassword 
                  ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                  : 'border-gray-300 focus:ring-[#6B9F54] focus:border-[#6B9F54]'
              }`}
              placeholder="••••••••"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
            >
              {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {errors.confirmPassword && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-1.5 text-sm text-red-600"
            >
              {errors.confirmPassword.message}
            </motion.p>
          )}
        </div>

        {/* Submit */}
        <motion.button
          type="submit"
          disabled={isLoading}
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
          className="w-full py-3 px-4 bg-[#6B9F54] hover:bg-[#5A8C43] text-white rounded-lg font-semibold text-base transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-50 shadow-sm"
        >
          {isLoading ? (
            <Loader2 className="animate-spin" size={20} />
          ) : (
            <>
              Reset Password
              <ArrowRight size={18} />
            </>
          )}
        </motion.button>
      </form>

      {/* Footer */}
      <div className="text-center">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-sm font-semibold text-[#6B9F54] hover:text-[#5A8C43] transition-colors"
        >
          <ArrowLeft size={16} />
          Back to login
        </Link>
      </div>
    </div>
  );
}
